import { FotoDoProduto } from './FotoDoProduto';
import { ROTULO_DA_ETIQUETA, ha, numeroDoCaso } from '../../auditoria';
import type { CasoDaFila, EtapaDaDivergencia, FilaDoAuditor } from '../../auditoria';
import { enderecoDoCaso } from './contexto-da-fila';
import { totalDePaginas } from './etapas';

/**
 * Divergências 3.0 — as três filas da Central (Prateleira · Estoque · Ajustes).
 * Mesmas classes `dv-*` da fila antiga; só as colunas mudam por etapa. Cada linha é um link para
 * o caso, levando o endereço da fila (`volta`) para a lateral usar a mesma etapa e os mesmos filtros.
 */

const plural = (n: number, um: string, varios: string) => `${String(n)} ${n === 1 ? um : varios}`;
/** Sem número, traço (nunca um "0" inventado). */
const numOuTraco = (n: number | null) => (n === null ? '—' : String(n));

export const ACAO_DA_ETAPA: Readonly<Record<EtapaDaDivergencia, string>> = {
  PRATELEIRA: 'Contar prateleira',
  ESTOQUE: 'Contar estoque',
  AJUSTE: 'Decidir',
  CONCLUIDA: 'Ver',
};

type PropsDaEtapa = { etapa: EtapaDaDivergencia; dados: FilaDoAuditor; volta: string };

function Tags({ c }: { c: CasoDaFila }) {
  const visiveis = c.etiquetas.filter((e) => e === 'ATRASADA' || e === 'REINCIDENTE');
  return (
    <>
      {visiveis.map((e) => (
        <span key={e} className={`dv-tag dv-tag-${e}`}>
          {ROTULO_DA_ETIQUETA[e]}
        </span>
      ))}
    </>
  );
}

function SituacaoDoAjuste({ c }: { c: CasoDaFila }) {
  return c.ajustePendente ? (
    <span className="dv-selo dv-selo-NOVA" title="Há ajuste a lançar no HOS">
      Ajuste pendente
    </span>
  ) : (
    <span className="dv-traco">—</span>
  );
}

/** O que o estoque tem: a contagem inteira (desde 13/10) ou "x de N" das buscas antigas. */
const estoqueTexto = (c: CasoDaFila) =>
  c.igorResultado === null
    ? '—'
    : c.igorResultado === 'NAO_VERIFICOU'
      ? 'não verificou'
      : c.igorResultado === 'CONTOU'
        ? numOuTraco(c.igorLocalizou)
        : `${numOuTraco(c.igorLocalizou)} de ${String(c.alvo)}`;

export function TabelaDeEtapa({ etapa, dados, volta }: PropsDaEtapa) {
  const { casos } = dados;
  const inicio = (dados.pagina - 1) * dados.porPagina;
  return (
    <div className="dv-tabela-cartao">
      <div className="dv-rolagem">
        <table className="dv-tabela dv-tabela-etapa">
          <thead>
            <tr>
              <th>Caso</th>
              <th>Produto</th>
              {etapa !== 'AJUSTE' && <th>Categoria</th>}
              <th>Balconista</th>
              {etapa === 'PRATELEIRA' && (
                <>
                  <th className="dv-dir">Prateleira</th>
                  <th className="dv-dir" title="Saldo do HOS no dia da contagem: só contexto">
                    HOS contagem
                  </th>
                  <th className="dv-dir" title="Saldo do HOS agora (cadastro de hoje)">
                    HOS agora
                  </th>
                  <th className="dv-dir">Repor</th>
                  <th className="dv-dir">Estoque contado</th>
                </>
              )}
              {etapa === 'ESTOQUE' && (
                <>
                  <th className="dv-dir">Prateleira contada</th>
                  <th className="dv-dir" title="Saldo do HOS no dia da contagem">
                    HOS contagem
                  </th>
                  <th className="dv-dir" title="Saldo do HOS agora (cadastro de hoje)">
                    HOS agora
                  </th>
                  <th className="dv-dir">Repor</th>
                </>
              )}
              {etapa === 'AJUSTE' && (
                <>
                  <th className="dv-dir">Prateleira contada</th>
                  <th className="dv-dir">Estoque contado</th>
                  <th className="dv-dir" title="Saldo do HOS no dia da contagem">
                    HOS contagem
                  </th>
                  <th className="dv-dir" title="Saldo do HOS agora (cadastro de hoje)">
                    HOS agora
                  </th>
                  <th>Situação</th>
                </>
              )}
              <th className="dv-dir">Aguardando</th>
              <th className="dv-dir">
                <span className="dv-sr">Ação</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {casos.map((c) => (
              <tr key={c.id}>
                <td className="dv-num">{numeroDoCaso(c.numero)}</td>
                <td>
                  <div className="dv-produto-com-foto">
                    <FotoDoProduto url={c.fotoProduto} nome={c.produto} tamanho={36} />
                    <div>
                      <div className="dv-produto">{c.produto}</div>
                      <div className="dv-codigo">
                        {c.codigo ?? 'sem código'}
                        <Tags c={c} />
                      </div>
                    </div>
                  </div>
                </td>
                {etapa !== 'AJUSTE' && <td className="dv-cinza">{c.setor}</td>}
                <td className={c.autoria ? '' : 'dv-dados'}>{c.autoria ?? 'não identificada'}</td>
                {etapa === 'PRATELEIRA' && (
                  <>
                    <td className="dv-dir">{numOuTraco(c.prateleira)}</td>
                    <td className="dv-dir dv-cinza">{numOuTraco(c.estoqueHosAbertura)}</td>
                    <td className="dv-dir">{numOuTraco(c.estoqueAtual)}</td>
                    <td className="dv-dir">{c.solicitado}</td>
                    <td className="dv-dir">{estoqueTexto(c)}</td>
                  </>
                )}
                {etapa === 'ESTOQUE' && (
                  <>
                    <td className="dv-dir">{numOuTraco(c.felipeEncontrou)}</td>
                    <td className="dv-dir dv-cinza">{numOuTraco(c.estoqueHosAbertura)}</td>
                    <td className="dv-dir">{numOuTraco(c.estoqueAtual)}</td>
                    <td className="dv-dir">{c.solicitado}</td>
                  </>
                )}
                {etapa === 'AJUSTE' && (
                  <>
                    <td className="dv-dir">{numOuTraco(c.felipeEncontrou)}</td>
                    <td className="dv-dir">{estoqueTexto(c)}</td>
                    <td className="dv-dir dv-cinza">{numOuTraco(c.estoqueHosAbertura)}</td>
                    <td className="dv-dir">{numOuTraco(c.estoqueAtual)}</td>
                    <td>
                      <SituacaoDoAjuste c={c} />
                    </td>
                  </>
                )}
                <td
                  className={`dv-dir ${c.atrasada ? 'dv-atrasada' : 'dv-cinza'}`}
                  title={c.atrasada ? 'Prazo vencido' : undefined}
                >
                  {ha(c.abertoEm)}
                </td>
                <td className="dv-dir">
                  <a
                    href={enderecoDoCaso(c.id, volta)}
                    className="dv-acao"
                    aria-label={`${ACAO_DA_ETAPA[etapa]} — caso ${numeroDoCaso(c.numero)}`}
                  >
                    {ACAO_DA_ETAPA[etapa]}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dv-rodape">
        <span>
          {inicio + 1}–{inicio + casos.length} de {plural(dados.total, 'caso', 'casos')}
        </span>
        <span className="dv-rodape-nota">
          {etapa === 'PRATELEIRA'
            ? 'Conte quanto tem na prateleira (em cima) · o estoque pode ser contado ao mesmo tempo'
            : etapa === 'ESTOQUE'
              ? 'Conte tudo o que tem no estoque (embaixo) · a prateleira pode ser contada ao mesmo tempo'
              : 'Com as duas contagens: foi erro do balconista ou não foi'}
        </span>
      </div>
    </div>
  );
}

export function CartoesDeEtapa({ etapa, dados, volta }: PropsDaEtapa) {
  return (
    <div className="dv-cartoes">
      {dados.casos.map((c) => (
        <a
          key={c.id}
          className="dv-cartao dv-cartao-link"
          href={enderecoDoCaso(c.id, volta)}
          aria-label={`${ACAO_DA_ETAPA[etapa]} — caso ${numeroDoCaso(c.numero)}, ${c.produto}`}
        >
          <div className="dv-c-topo">
            <span className="dv-num">{numeroDoCaso(c.numero)}</span>
            {c.ajustePendente && <span className="dv-selo dv-selo-NOVA">Ajuste pendente</span>}
            <span className="dv-esp" />
            <span className={c.atrasada ? 'dv-atrasada' : 'dv-c-ha'}>{ha(c.abertoEm)}</span>
          </div>
          <div className="dv-produto-com-foto">
            <FotoDoProduto url={c.fotoProduto} nome={c.produto} tamanho={44} />
            <div>
              <div className="dv-c-produto">{c.produto}</div>
              <div className="dv-codigo">{c.autoria ?? 'Balconista não identificada'}</div>
            </div>
          </div>
          <div className="dv-c-numeros">
            {etapa === 'PRATELEIRA' && (
              <>
                <div>
                  <span>PRATEL.</span>
                  <b>{numOuTraco(c.prateleira)}</b>
                </div>
                <div>
                  <span>SOLIC.</span>
                  <b>{c.solicitado}</b>
                </div>
              </>
            )}
            {etapa === 'ESTOQUE' && (
              <>
                <div>
                  <span>PRATEL.</span>
                  <b>{numOuTraco(c.felipeEncontrou)}</b>
                </div>
                <div>
                  <span>REPOR</span>
                  <b>{c.solicitado}</b>
                </div>
              </>
            )}
            {etapa === 'AJUSTE' && (
              <>
                <div>
                  <span>PRATEL.</span>
                  <b>{numOuTraco(c.felipeEncontrou)}</b>
                </div>
                <div>
                  <span>ESTOQUE</span>
                  <b>{estoqueTexto(c)}</b>
                </div>
              </>
            )}
            <div>
              <span>HOS CONT.</span>
              <b className="dv-cinza">{numOuTraco(c.estoqueHosAbertura)}</b>
            </div>
            <div>
              <span>HOS AGORA</span>
              <b>{numOuTraco(c.estoqueAtual)}</b>
            </div>
            <span className="dv-esp" />
            <span className="dv-c-acao-etapa">{ACAO_DA_ETAPA[etapa]} →</span>
          </div>
        </a>
      ))}
      <div className="dv-c-rodape">
        {dados.casos.length} de {plural(dados.total, 'caso', 'casos')}
      </div>
    </div>
  );
}

export function Paginacao({ dados, aoIr }: { dados: FilaDoAuditor; aoIr: (n: number) => void }) {
  const paginas = totalDePaginas(dados.total, dados.porPagina);
  if (paginas <= 1) return null;
  return (
    <nav className="dv-paginacao" aria-label="Páginas da fila">
      <button
        type="button"
        className="dv-botao"
        disabled={dados.pagina <= 1}
        onClick={() => aoIr(dados.pagina - 1)}
      >
        Anterior
      </button>
      <span>
        Página {dados.pagina} de {paginas}
      </span>
      <button
        type="button"
        className="dv-botao"
        disabled={dados.pagina >= paginas}
        onClick={() => aoIr(dados.pagina + 1)}
      >
        Próxima
      </button>
    </nav>
  );
}
